import type { Bloc, Champ } from '@funo/grammaire'
import type { FicheEvenement } from '../../serveur/src/evenements/index.ts'
import { cleLocale, ConditionEditor } from './formulaires.tsx'
import {
  prixInventaire,
  UNITES_INVENTAIRE,
  type ItemInventaire,
} from './inventaire-types.ts'
type Offres = Extract<Bloc, { bloc: 'offres' }>
export function OffresEditor({
  bloc,
  changer,
  articles,
  fiche,
  champs,
  onInventaire,
}: {
  bloc: Offres
  changer(b: Offres): void
  articles: ItemInventaire[]
  fiche: FicheEvenement
  champs: Array<{
    fait: string
    nom: string
    type?: string
    options?: Champ['options']
  }>
  onInventaire?(id: string): void
}) {
  const principal = articles.find((i) => i.id === fiche.inventaire_item_id)
  const disponibles = articles.filter(
    (i) =>
      i.fiche.actif &&
      i.id !== fiche.inventaire_item_id &&
      !bloc.options.some((o) => o.item_id === i.id) &&
      (fiche.type !== 'achat' ||
        (!i.ressource_id && !['heure', 'jour'].includes(i.fiche.unite))),
  )
  const modifier = (cle: string, valeurs: Partial<Offres['options'][number]>) =>
    changer({
      ...bloc,
      options: bloc.options.map((o) =>
        o.cle === cle ? { ...o, ...valeurs } : o,
      ),
    })
  return (
    <div class="of-admin">
      <style>{`.of-admin{min-width:0}.of-admin-ligne{border:1px solid #ece1f0;border-radius:12px;padding:17px;margin:13px 0;background:#fefdfe}.of-admin-entete{display:flex;gap:12px;align-items:flex-start;justify-content:space-between}.of-admin-entete strong{font-size:14px}.of-admin-entete small{display:block;color:#6b5a70;font-size:12px;margin-top:6px}.of-admin-entete button{flex-shrink:0}.of-admin-details{margin-top:15px}.of-admin .of-admin-details summary{font-size:12px;color:#391d44;cursor:pointer;margin-bottom:12px}.of-admin-vide{border:1px dashed #e2d0ed;border-radius:12px;padding:23px;color:#836d8b;font-size:13px;margin:15px 0!important}.of-admin-gl{font-size:11px;color:#7a6682!important}.of-admin-limit{max-width:210px;margin-top:15px}`}</style>
      <p class="wf-aide">
        {fiche.type === 'achat'
          ? 'Le client peut ajouter des éléments à sa commande.'
          : 'Les équipements complètent la réservation sur la même période que l’élément principal. Leurs quantités sont vérifiées et retenues sur ce créneau.'}{' '}
        Chaque ligne conserve son tarif et son compte GL.
      </p>
      {bloc.quantite_principale_maximum !== undefined ? (
        <label>
          Quantité maximale de l’article principal par commande
          <input
            type="number"
            min="1"
            max={Math.min(1000, principal?.fiche.quantite ?? 1000)}
            value={bloc.quantite_principale_maximum}
            onInput={(e) =>
              changer({
                ...bloc,
                quantite_principale_maximum: Number(e.currentTarget.value),
              })
            }
          />
        </label>
      ) : null}
      <label>
        Ajouter une offre supplémentaire
        <select
          value=""
          onChange={(e) => {
            const i = articles.find((i) => i.id === e.currentTarget.value)
            if (!i) return
            changer({
              ...bloc,
              options: [
                ...bloc.options,
                {
                  cle: cleLocale('option'),
                  item_id: i.id,
                  nom: i.fiche.nom,
                  description: i.fiche.description,
                  montant_cents: i.fiche.prix_cents,
                  unite: i.fiche.unite,
                  quantite_maximum: Math.min(1000, i.fiche.quantite),
                  ...(i.ressource_id ? { ressource_id: i.ressource_id } : {}),
                  ...(i.fiche.compte_gl
                    ? { compte_gl: i.fiche.compte_gl }
                    : {}),
                },
              ],
            })
          }}
        >
          <option value="">Choisir dans l’inventaire…</option>
          {disponibles.map((i) => (
            <option key={i.id} value={i.id}>
              {i.fiche.nom} · {prixInventaire(i.fiche.prix_cents)}{' '}
              {UNITES_INVENTAIRE[i.fiche.unite]}
            </option>
          ))}
        </select>
      </label>
      {!bloc.options.length ? (
        <p class="of-admin-vide">
          Proposez un projecteur, des tables ou du matériel pour compléter la
          réservation. Le client choisira les quantités nécessaires.
        </p>
      ) : null}
      {bloc.options.map((o) => {
        const i = articles.find((i) => i.id === o.item_id)
        return (
          <article class="of-admin-ligne" key={o.cle}>
            <div class="of-admin-entete">
              <div>
                <strong>{o.nom}</strong>
                <small>
                  {prixInventaire(o.montant_cents)} {UNITES_INVENTAIRE[o.unite]}
                </small>
                <small class="of-admin-gl">
                  {o.compte_gl?.compte_credit
                    ? `Compte GL · ${o.compte_gl!.compte_credit}`
                    : 'Compte GL à configurer dans l’inventaire'}
                </small>
                {i && !i.fiche.actif ? (
                  <small>
                    Élément désactivé : remplacez-le avant publication.
                  </small>
                ) : null}
              </div>
              <button
                class="fb-lien fb-danger"
                type="button"
                aria-label={'Retirer ' + o.nom}
                onClick={() =>
                  changer({
                    ...bloc,
                    options: bloc.options.filter((v) => v.cle !== o.cle),
                  })
                }
              >
                Retirer
              </button>
            </div>
            <label class="of-admin-limit">
              Quantité maximale
              <input
                type="number"
                min="1"
                max={i?.ressource_id ? Math.min(i.fiche.quantite, 1000) : 1000}
                value={o.quantite_maximum}
                onInput={(e) =>
                  modifier(o.cle, {
                    quantite_maximum: Number(e.currentTarget.value),
                  })
                }
              />
            </label>
            <details class="of-admin-details">
              <summary>Conditions d’affichage</summary>
              <ConditionEditor
                valeur={o.si}
                champs={champs}
                changer={(si) => modifier(o.cle, { si })}
              />
            </details>
            {onInventaire ? (
              <button
                class="fb-lien"
                type="button"
                onClick={() => onInventaire(o.item_id)}
              >
                Ouvrir l’élément et ses comptes GL ↗
              </button>
            ) : null}
          </article>
        )
      })}
      {fiche.type === 'achat' ? (
        <p class="wf-aide">
          Seuls les articles vendus sans créneau sont proposés ici. Les
          quantités sont des limites par commande.
        </p>
      ) : null}
    </div>
  )
}
